/**
 * KAN-718: форма ящика для переписки (секция на вкладке "Интеграции"). По умолчанию письма клиентам уходят с
 * общего ящика платформы; здесь организация подключает свой ящик (SMTP для
 * отправки, IMAP для приёма ответов), проверяет его, шлёт тестовое письмо
 * или возвращается на общий ящик.
 */
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Mail, PlugZap, RotateCcw, Save, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import { ConfirmDialog } from '@/components/telegram/ConfirmDialog';
import { useEmailMailbox, useEmailMailboxMutations } from '@/hooks/useEmailMailbox';
import { extractErrorMessage } from '@/lib/extractErrorMessage';
import {
  MAILBOX_PRESETS,
  applyPreset,
  formFromMailbox,
  mailboxBody,
  missingMailboxFields,
  type MailboxFormState,
} from '@/lib/emailMailboxForm';
import type { MailboxCheckResult } from '@/types/emailMailbox';
import { MailboxCheckBadge } from './MailboxCheckBadge';
import { MailboxServerFields } from './MailboxServerFields';
import { TestSendDialog } from './TestSendDialog';

const RESET_VIEW = {
  title: 'Вернуться на общий ящик?',
  description: 'Настройки своего ящика будут удалены, письма клиентам снова пойдут с общего ящика платформы.',
  confirm: 'Вернуться',
};

const PRESET_OPTIONS = MAILBOX_PRESETS.map((p) => ({ value: p.id, label: p.label }));

export function MailboxSettings(): JSX.Element {
  const { data: mailbox, isLoading, isError } = useEmailMailbox();
  const { save, reset, check } = useEmailMailboxMutations();
  const [form, setForm] = useState<MailboxFormState>(() => formFromMailbox(undefined));
  const [presetId, setPresetId] = useState('');
  const [draftCheck, setDraftCheck] = useState<MailboxCheckResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmReset, setConfirmReset] = useState(false);
  const [testOpen, setTestOpen] = useState(false);

  useEffect(() => {
    setForm(formFromMailbox(mailbox));
    setDraftCheck(null);
  }, [mailbox]);

  if (isLoading) {
    return <p className="text-sm text-gray-500" data-testid="mailbox-loading">Загрузка...</p>;
  }
  if (isError || !mailbox) {
    return (
      <p className="text-sm text-red-700" data-testid="mailbox-error">
        Не удалось загрузить настройки почты
      </p>
    );
  }

  const patch = (next: Partial<MailboxFormState>): void => setForm((prev) => ({ ...prev, ...next }));
  const preset = MAILBOX_PRESETS.find((p) => p.id === presetId);

  const choosePreset = (id: string): void => {
    setPresetId(id);
    const chosen = MAILBOX_PRESETS.find((p) => p.id === id);
    if (chosen) setForm((prev) => applyPreset(prev, chosen));
  };

  const runCheck = (): void => {
    setError(null);
    setDraftCheck(null);
    check.mutate(mailboxBody(form), {
      onSuccess: setDraftCheck,
      onError: (err) => setError(extractErrorMessage(err, 'Не удалось проверить подключение')),
    });
  };

  const runSave = (): void => {
    const missing = missingMailboxFields(form);
    if (missing.length) {
      setError(`Заполните: ${missing.join(', ')}`);
      return;
    }
    setError(null);
    save.mutate(mailboxBody(form), {
      onSuccess: () => toast.success('Настройки почты сохранены'),
      onError: (err) => setError(extractErrorMessage(err, 'Не удалось сохранить настройки почты')),
    });
  };

  const runReset = (): void => {
    setConfirmReset(false);
    reset.mutate(undefined, {
      onSuccess: () => toast.success('Письма снова уходят с общего ящика'),
      onError: (err) => setError(extractErrorMessage(err, 'Не удалось вернуться на общий ящик')),
    });
  };

  return (
    <section data-testid="mailbox-settings" className="max-w-3xl space-y-5">
      <div className="rounded-lg border bg-gray-50 p-4" data-testid="mailbox-status">
        <div className="flex items-start gap-3">
          <Mail className="mt-0.5 h-5 w-5 shrink-0 text-gray-500" />
          <div className="space-y-2 text-sm">
            {mailbox.configured ? (
              <p>
                Письма уходят с вашего ящика <span className="font-medium">{mailbox.address}</span>
              </p>
            ) : (
              <p>
                Письма уходят с общего ящика{' '}
                <span className="font-medium">{mailbox.platform_address}</span>. Подключите свой
                ящик, чтобы клиенты видели ваш адрес и ответы приходили вам.
              </p>
            )}
            {mailbox.configured && (
              <MailboxCheckBadge result={mailbox.last_check} testId="mailbox-last-check" />
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="space-y-1">
          <Label htmlFor="mail-address" className="text-xs">Адрес ящика</Label>
          <Input
            id="mail-address"
            type="email"
            value={form.address}
            placeholder="sales@company.ru"
            onChange={(e) => patch({ address: e.target.value })}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="mail-from-name" className="text-xs">Имя отправителя</Label>
          <Input
            id="mail-from-name"
            value={form.from_name}
            placeholder="ООО Компания"
            onChange={(e) => patch({ from_name: e.target.value })}
          />
        </div>
      </div>

      <div className="space-y-1">
        <Label htmlFor="mail-preset" className="text-xs">Почтовый сервис</Label>
        <NativeSelect
          id="mail-preset"
          value={presetId}
          placeholder="Выберите, чтобы заполнить серверы и порты"
          options={PRESET_OPTIONS}
          onChange={(e) => choosePreset(e.target.value)}
        />
        {preset && (
          <p className="text-xs text-gray-500" data-testid="mail-preset-hint">{preset.hint}</p>
        )}
      </div>

      <MailboxServerFields
        form={form}
        onChange={patch}
        hasSmtpPassword={mailbox.configured && mailbox.has_smtp_password}
        hasImapPassword={mailbox.configured && mailbox.has_imap_password}
      />

      {draftCheck && <MailboxCheckBadge result={draftCheck} testId="mailbox-draft-check" />}
      {error && (
        <p className="text-sm text-red-700" data-testid="mailbox-form-error">{error}</p>
      )}

      <div className="flex flex-wrap gap-2">
        <Button variant="outline" data-testid="mailbox-check" disabled={check.isPending} onClick={runCheck}>
          <PlugZap className="mr-1 h-4 w-4" />
          {check.isPending ? 'Проверка...' : 'Проверить подключение'}
        </Button>
        <Button data-testid="mailbox-save" disabled={save.isPending} onClick={runSave}>
          <Save className="mr-1 h-4 w-4" />
          {save.isPending ? 'Сохранение...' : 'Сохранить'}
        </Button>
        {mailbox.configured && (
          <>
            <Button variant="outline" data-testid="mailbox-test-send" onClick={() => setTestOpen(true)}>
              <Send className="mr-1 h-4 w-4" />
              Отправить тестовое письмо
            </Button>
            <Button
              variant="outline"
              className="text-red-700"
              data-testid="mailbox-reset"
              disabled={reset.isPending}
              onClick={() => setConfirmReset(true)}
            >
              <RotateCcw className="mr-1 h-4 w-4" />
              Вернуться на общий ящик
            </Button>
          </>
        )}
      </div>

      <TestSendDialog open={testOpen} onOpenChange={setTestOpen} defaultTo={mailbox.address} />
      <ConfirmDialog
        open={confirmReset}
        onOpenChange={setConfirmReset}
        view={RESET_VIEW}
        onConfirm={runReset}
        testId="mailbox-reset-confirm"
      />
    </section>
  );
}
